import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { vehicles, locations } from "@data/vehicles";
import axios from "axios";

const rentalVehicleSchema = z.object({
  type: z.literal("vehicleRental"),
  vehicle: z.string().min(1, "Vehicle selection is required"),
  passengers: z
    .number()
    .min(1, "At least 1 passenger is required")
    .max(12, "Maximum 12 passengers allowed"),
  pickupLocation: z.string().min(1, "Pickup location is required"),
  pickupDate: z
    .string()
    .min(1, "Pickup date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Pickup date cannot be before today"),
  pickupTime: z.string().min(1, "Pickup time is required"),
  returnLocation: z.string().min(1, "Return location is required"),
  returnDate: z
    .string()
    .min(1, "Return date is required")
    .refine((date) => {
      const selectedDate = new Date(date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= today;
    }, "Return date cannot be before today"),
  returnTime: z.string().min(1, "Return time is required"),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  phoneNumber: z.string().min(1, "Phone number is required"),
});

type RentalVehicleFormType = z.infer<typeof rentalVehicleSchema>;

interface RentalVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicleType: string;
}

const RentalVehicleModal: React.FC<RentalVehicleModalProps> = ({
  isOpen,
  onClose,
  vehicleType,
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const rentalVehicleEndpoint = "/vehicle-rental";

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<RentalVehicleFormType>({
    resolver: zodResolver(rentalVehicleSchema),
    defaultValues: {
      type: "vehicleRental",
      vehicle: "",
      passengers: 1,
      pickupLocation: "",
      pickupDate: "",
      pickupTime: "",
      returnLocation: "",
      returnDate: "",
      returnTime: "",
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    },
  });

  const filteredVehicles = vehicles.filter(
    (vehicle) => vehicle.type === vehicleType,
  );

  const onSubmit = async (data: RentalVehicleFormType) => {
    console.log("Form submission attempted");
    console.log("Form data:", data);
    console.log("Form errors:", errors);
    console.log("Is submitting:", isSubmitting);

    try {
      await axios.post(
        `${import.meta.env.VITE_CONTACT_API_URL}${rentalVehicleEndpoint}`,
        {
          ...data,
          timestamp: new Date().toISOString(),
        },
      );

      console.log("Booking successful!");
      setIsSubmitted(true);
      reset();
    } catch (error) {
      console.error("Booking error:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-transparent z-50 flex items-center justify-center p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border-2 border-gray-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-primaryText">
              Vehicle Booking
            </h2>
            <button
              onClick={onClose}
              className="cursor-pointer text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-primaryText mb-2">
                  Booking Request Sent!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your vehicle rental request has been successfully submitted.
                  We'll get back to you shortly with confirmation and
                  availability details.
                </p>
              </div>
              <button
                onClick={onClose}
                className="cursor-pointer px-6 py-3 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Vehicle Selection */}
              <div>
                <label className="block text-sm font-medium text-primaryText mb-2">
                  Vehicle *
                </label>
                <select
                  {...register("vehicle")}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.vehicle ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select a vehicle</option>
                  {filteredVehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.name}>
                      {vehicle.name}
                    </option>
                  ))}
                </select>
                {errors.vehicle && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.vehicle.message}
                  </p>
                )}
              </div>

              {/* Number of Passengers */}
              <div>
                <label className="block text-sm font-medium text-primaryText mb-2">
                  Number of Passengers *
                </label>
                <input
                  type="number"
                  min="1"
                  max="12"
                  {...register("passengers", { valueAsNumber: true })}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.passengers ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.passengers && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.passengers.message}
                  </p>
                )}
              </div>

              {/* Pickup Location */}
              <div>
                <label className="block text-sm font-medium text-primaryText mb-2">
                  Pickup Location *
                </label>
                <select
                  {...register("pickupLocation")}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.pickupLocation ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select pickup location</option>
                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                {errors.pickupLocation && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.pickupLocation.message}
                  </p>
                )}
              </div>

              {/* Pickup Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Pickup Date *
                  </label>
                  <input
                    type="date"
                    {...register("pickupDate")}
                    min={new Date().toISOString().split("T")[0]}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.pickupDate ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.pickupDate && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.pickupDate.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Pickup Time *
                  </label>
                  <input
                    type="time"
                    {...register("pickupTime")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.pickupTime ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.pickupTime && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.pickupTime.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Return Location */}
              <div>
                <label className="block text-sm font-medium text-primaryText mb-2">
                  Return Location *
                </label>
                <select
                  {...register("returnLocation")}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                    errors.returnLocation ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select return location</option>
                  {locations.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                {errors.returnLocation && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.returnLocation.message}
                  </p>
                )}
              </div>

              {/* Return Date and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Return Date *
                  </label>
                  <input
                    type="date"
                    {...register("returnDate")}
                    min={new Date().toISOString().split("T")[0]}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.returnDate ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.returnDate && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.returnDate.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Return Time *
                  </label>
                  <input
                    type="time"
                    {...register("returnTime")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.returnTime ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.returnTime && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.returnTime.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Personal Information Section */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="text-lg font-semibold text-primaryText mb-4">
                  Personal Information
                </h3>

                {/* First Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-primaryText mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      {...register("firstName")}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.firstName ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.firstName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.firstName.message}
                      </p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label className="block text-sm font-medium text-primaryText mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      {...register("lastName")}
                      className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                        errors.lastName ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {errors.lastName && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.lastName.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register("email")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div>
                  <label className="block text-sm font-medium text-primaryText mb-2">
                    Phone Number / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    {...register("phoneNumber")}
                    className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary focus:border-primary ${
                      errors.phoneNumber ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                  {errors.phoneNumber && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.phoneNumber.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="cursor-pointer flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="cursor-pointer flex-1 px-6 py-3 bg-primary text-white rounded-md hover:bg-opacity-90 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? "Submitting..." : "Book Now"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RentalVehicleModal;
